"use client"

import React, { useMemo } from "react"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import TextLinkWithArrow from "@/components/ui/components/text-link-with-arrow"
import { DiscountActiveLine } from "@/components/account/profile/common/Discount"
import { determineSegments } from "@/components/account/users/users-helper"
import BasicCard from "@/components/common/BasicCard"
import type { DashboardBlok } from "./types"

interface AccountDashboardProfileCardProps {
  accountContext: AccountContext
  blok: DashboardBlok
}

function determineInitials(accountContext: AccountContext) {
  const firstName = accountContext?.userData?.firstName ?? ""
  const lastName = accountContext?.userData?.lastName ?? ""

  if (firstName && lastName && firstName.length > 0 && lastName.length > 0) {
    return firstName.charAt(0).toUpperCase() + "." + lastName.charAt(0).toUpperCase() + "."
  } else {
    return ""
  }
}

function determineRoleString(accountContext: AccountContext) {
  if (accountContext?.roles && accountContext?.roles?.userRoles && accountContext?.roles?.userRoles.length > 0) {
    const roleNames = accountContext?.roles?.userRoles.map((role) => role.roleDisplayName)

    if (roleNames && roleNames.length > 0) {
      return roleNames.join(", ")
    }
  }

  return ""
}

export default function AccountDashboardProfileCard(props: AccountDashboardProfileCardProps) {
  const accountContext: AccountContext = props.accountContext
  const generalDiscount: number | undefined = accountContext?.customerData?.generalDiscount // https://udobaer.atlassian.net/browse/UBW-964

  const segmentIds: string[] = useMemo(() => {
    return determineSegments(props.accountContext?.customerData)
  }, [props.accountContext])

  const roleString: string = useMemo(() => {
    return determineRoleString(props.accountContext)
  }, [props.accountContext])

  const initialsString: string = useMemo(() => {
    return determineInitials(props.accountContext)
  }, [props.accountContext])
  return (
    <>
      <BasicCard className={"text-sm"}>
        <div className={`flex w-full flex-row items-center justify-between gap-2`}>
          <h2 className={"text-lg font-bold"}>{props.blok.my_profile_headline}</h2>
          <TextLinkWithArrow
            prefetch={false}
            href={getLinkWithPrefixFromURLAndLocale("/account/profile", props.accountContext.sbLocale)}
          >
            {props.blok.got_to_profile_text}
          </TextLinkWithArrow>
        </div>
        <div className={"flex flex-row gap-2"}>
          <div className={"flex flex-col justify-start p-2"}>
            <div
              className={`
                flex size-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-white
              `}
            >
              <p>{initialsString}</p>
            </div>
          </div>
          <div className={`flex grow flex-col gap-1 text-sm sm:text-base`}>
            <div className={`flex gap-1 md:text-lg`}>
              <p>{accountContext.userData?.firstName}</p>
              <p>{accountContext.userData?.lastName}</p>
            </div>
            <p
              className={cn("hidden text-primary", {
                block: roleString && roleString.length > 0,
              })}
            >
              {roleString}
            </p>
            <p>{accountContext.customerData?.companyName}</p>

            <p>{accountContext.userData?.email}</p>

            {accountContext.isB2B &&
              ((generalDiscount && generalDiscount > 0) || segmentIds.includes("CG_B2B_REBATE_CUSTOMER")) && (
                <DiscountActiveLine
                  discountLabel={props.blok?.general_discount_text}
                  activeText={props.blok?.general_discount_active_text}
                />
              )}
          </div>
        </div>
      </BasicCard>
    </>
  )
}
